import React, { useState } from "react";
import { PageHero } from "@/components/PageParts";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", preferred_time: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/inquiries", { ...form, inquiry_type: "general" });
      setDone(true);
      toast.success("Thank you — we'll be in touch within 24 hours.");
      setForm({ name: "", email: "", phone: "", preferred_time: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <PageHero
        eyebrow="Book a Free Chat"
        title="Let's begin the conversation."
        subtitle="A no-obligation 20-minute call to talk about the person, the story, and what could be possible."
        image="https://images.pexels.com/photos/8848784/pexels-photo-8848784.jpeg"
        testid="contact-hero"
      />
      <section className="section-pad bg-offwhite">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-12">
          <div>
            <p className="eyebrow mb-3">Get in Touch</p>
            <h2 className="font-serif text-4xl mb-6">Warmly. Unhurried. Personally.</h2>
            <p className="text-warmgrey mb-10 text-lg">
              We respond to every enquiry personally, usually within a few hours during the day. There's no scripts here — just a real conversation about your family.
            </p>
            <ul className="space-y-5">
              <li className="flex gap-4 items-start"><Mail style={{ color: "#C9A961" }} /><div><p className="text-sm text-warmgrey-muted">Email</p><a href="mailto:info@familylegacyfilms.com.au" className="text-headline hover:text-primary">info@familylegacyfilms.com.au</a></div></li>
              <li className="flex gap-4 items-start"><Phone style={{ color: "#C9A961" }} /><div><p className="text-sm text-warmgrey-muted">Phone</p><a href="tel:+61460017777" className="text-headline hover:text-primary">0460 017 777</a></div></li>
              <li className="flex gap-4 items-start"><MapPin style={{ color: "#C9A961" }} /><div><p className="text-sm text-warmgrey-muted">Serving</p><p className="text-headline">Sydney &amp; Canberra, Australia</p></div></li>
              <li className="flex gap-4 items-start"><Clock style={{ color: "#C9A961" }} /><div><p className="text-sm text-warmgrey-muted">Response</p><p className="text-headline">Within 24 hours, always.</p></div></li>
            </ul>
          </div>

          <form onSubmit={submit} className="bg-white p-8 rounded-xl border border-border-warm" data-testid="contact-form">
            {done ? (
              <div className="text-center py-10" data-testid="contact-thanks">
                <h3 className="font-serif text-3xl mb-3">Thank you.</h3>
                <p className="text-warmgrey">Your message is on its way. We'll come back to you personally.</p>
              </div>
            ) : (
              <>
                <input required data-testid="contact-name" className="input-field mb-4" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input required data-testid="contact-email" type="email" className="input-field mb-4" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <input data-testid="contact-phone" className="input-field mb-4" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                <input data-testid="contact-time" className="input-field mb-4" placeholder="Preferred time to call" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} />
                <textarea required data-testid="contact-message" className="input-field mb-6 min-h-[140px]" placeholder="Tell us a little about your family..." value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                <button disabled={loading} className="btn-primary w-full justify-center" data-testid="contact-submit">{loading ? "Sending..." : "Send Message"}</button>
              </>
            )}
          </form>
        </div>
      </section>
    </div>
  );
}
